import { Box, Typography } from '@mui/material'
import React, { memo, useEffect, useMemo, useState } from 'react'
import { TradingSession } from '../../proto/generated/trading-calendar'
import { formatEpochTime } from '../../util/dateTime'
import { marketStatusBlue, marketStatusGreen, marketStatusOrange } from '../../assets/colors'
import { NonInteractiveTooltip } from '../../components/custom'

export interface SliderProps {
    color: string,
    width: string,
    text: string
}

export interface MarketStatusBarProps {
    tradingSession: TradingSession
}

const Slider = memo((props: SliderProps) => {

    const tooltipContent = useMemo(() => (
        <Typography variant="caption">{props.text}</Typography>
    ), [props.text])

    return (
        <NonInteractiveTooltip title={tooltipContent} arrow={true} placement="top">
            <div style={{ width: props.width, backgroundColor: props.color, height: '8px', borderRadius: '6px' }} />
        </NonInteractiveTooltip>
    )
})

Slider.displayName = 'Slider'

const MarketStatusBar = (props: MarketStatusBarProps) => {
    const [totalPercentage, setTotalPercentage] = useState(0)
    const tradingSession = props.tradingSession

    useEffect(() => {
        const openPercentage = calculatePercentageWidth(tradingSession.startTime, tradingSession.endTime)
        const preOpenPercentage = calculatePercentageWidth(tradingSession.preOpenStartTime, tradingSession.preOpenEndTime)
        const postOpenPercentage = calculatePercentageWidth(tradingSession.postCloseStartTime, tradingSession.postCloseEndTime)
        setTotalPercentage(openPercentage + preOpenPercentage + postOpenPercentage)
    }, [])

    function calculatePercentageWidth(startTime: number, endTime: number) {
        const startDate = new Date(startTime)
        const endDate = new Date(endTime)
        const timeInterval = ((endDate as any) - (startDate as any)) / (60 * 1000)
        return (timeInterval / (24 * 60)) * 100
    }

    function getCurrentDay() {
        const currentTime = new Date()
        const daysOfWeek = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
        return daysOfWeek[currentTime.getDay()].toUpperCase()
    }

    return (
        <Box sx={{ width: '100%', display: 'flex', alignItems: 'center', gap: '4px' }}>
            <Typography variant={'caption'}>{getCurrentDay()}</Typography>
            <Box sx={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Slider color="#E0E3EB" width={`${100 - totalPercentage / 2}%`} text={`Market closed`} />
                <Slider color={marketStatusOrange}
                        width={`calc(${calculatePercentageWidth(tradingSession.preOpenStartTime, tradingSession.preOpenEndTime)}% + 5px)`}
                        text={`Pre-market ${formatEpochTime(tradingSession.preOpenStartTime)} - ${formatEpochTime(tradingSession.preOpenEndTime)}`} />
                <Slider color={marketStatusGreen}
                        width={`${calculatePercentageWidth(tradingSession.startTime, tradingSession.endTime)}%`}
                        text={`Market open ${formatEpochTime(tradingSession.startTime)} - ${formatEpochTime(tradingSession.endTime)}`} />
                <Slider color={marketStatusBlue}
                        width={`calc(${calculatePercentageWidth(tradingSession.postCloseStartTime, tradingSession.postCloseEndTime)}% + 5px)`}
                        text={`Post-market ${formatEpochTime(tradingSession.postCloseStartTime)} - ${formatEpochTime(tradingSession.postCloseEndTime)}`} />
                <Slider color="#E0E3EB" width={`${100 - totalPercentage / 2}%`} text={`Market closed`} />
            </Box>
        </Box>
    )
}


export default memo(MarketStatusBar)